import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SmartLink from '../common/SmartLink'
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../lib/supabaseClient';
import { useRegisterEditor } from '../lib/EditorContext';
import { defaultContent } from '../lib/defaultContent';

const { FiCoffee, FiHeadphones, FiWind, FiPlayCircle } = FiIcons;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const defaultSelfCareStrategies = [
  {
    id: 1,
    title: "Micro-Breaks",
    iconName: "FiCoffee",
    desc: "Take 5 minutes before getting out of the car or hide in the pantry. Small moments of quiet are essential for resetting your nervous system.",
    iconBg: "bg-blue-100",
    iconBorder: "border-blue-200",
    iconColor: "text-blue-600"
  },
  {
    id: 2,
    title: "Sensory Shift",
    iconName: "FiHeadphones",
    desc: "Use noise-canceling headphones for 10 minutes or splash cold water on your face. Disrupt the sensory overload before it leads to burnout.",
    iconBg: "bg-yellow-100",
    iconBorder: "border-yellow-200",
    iconColor: "text-yellow-600"
  },
  {
    id: 3,
    title: "Breath Anchors",
    iconName: "FiWind",
    desc: "Inhale for 4, exhale for 6. Focus on your breathing to move from a state of reaction into a state of grounded intention.",
    iconBg: "bg-red-100",
    iconBorder: "border-red-200",
    iconColor: "text-brand-rose"
  }
];

const selfCareExamples = [
  "Take 10 min before getting out of the car",
  "5 slow breaths",
  "Step into another room",
  "Cold water on face",
  "Music to shift mood",
  "Drink water + eat a meal",
  "Get outside 5 min",
  "Write to unload",
  "Text a friend who gets it",
  "Trade childcare time",
  "Say 'no' to one thing",
  "Go to bed 20 min earlier"
];

export default function SelfCare() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('content')
          .eq('page_id', 'selfcare')
          .single();

        if (error) {
          console.warn('Could not fetch self-care content, using defaults.', error.message);
        } else if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Error fetching self-care content:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, []);

  const title = content?.title || "Practical Self-Care";
  const subtitle = content?.subtitle || "Actionable ideas to recharge and sustain your well-being. Self-care isn't a luxury; it's the foundation of effective parenting.";
  const overline = content?.overline || "Prioritize Your Peace";

  const currentStrategies = content?.selfCareStrategies || defaultSelfCareStrategies;
  const currentExamples = content?.selfCareExamples || selfCareExamples;

  const wantToLearnMore = content?.wantToLearnMore || defaultContent.selfcare.wantToLearnMore;

  const handleSaveSuccess = (newContent) => {
    setContent(newContent);
  };

  useRegisterEditor(
    'selfcare',
    { title, subtitle, overline, selfCareStrategies: currentStrategies, selfCareExamples: currentExamples, wantToLearnMore },
    handleSaveSuccess,
    'Self Care Page'
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" exit="hidden" className="py-12 lg:py-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#333] dark:text-[#fff] font-bold tracking-widest uppercase text-xs mb-3 block">{overline}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-[#333] dark:text-[#fff] mb-6">{title}</h1>
          <p className="text-lg text-[#333] dark:text-[#fff]">
            {subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {currentStrategies.map((item) => (
            <motion.div key={item.id} variants={fadeUp} className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100 flex flex-col items-center text-center hover:shadow-lg transition-all duration-300">
              <div className={`w-16 h-16 ${item.iconBg} dark:bg-brand-slate rounded-full flex items-center justify-center mb-6 shadow-sm border ${item.iconBorder}`}>
                <SafeIcon icon={FiIcons[item.iconName] || FiIcons.FiCoffee} className={`${item.iconColor} w-8 h-8`} />
              </div>
              <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-4">{item.title}</h3>
              <p className="text-[#333] dark:text-[#fff] leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div variants={fadeUp} className="mb-20 max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-[#333] dark:text-[#fff] mb-8 text-center">Self-Care (Non-Instagram) List</h2>
          <div className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100 grid grid-cols-1 md:grid-cols-2 gap-4">
            {currentExamples.map((example, idx) => (
              <div key={idx} className="flex items-start gap-4 p-4 rounded-xl hover:bg-sand-50 dark:hover:bg-brand-slate/50 transition-colors">
                <div className="mt-1 text-brand-rose">
                  <span className="text-xl">🤍</span>
                </div>
                <p className="text-[#333] dark:text-[#fff] font-medium">{example}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="bg-brand-rose text-white rounded-[3rem] p-10 md:p-16 text-center max-w-4xl mx-auto shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">{wantToLearnMore.title}</h2>
            <p className="text-lg mb-10 max-w-2xl mx-auto opacity-90">
              {wantToLearnMore.description}
            </p>
            <SmartLink to={wantToLearnMore.buttonLink} className="inline-flex items-center gap-2 px-10 py-4 bg-white text-brand-rose rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-lg">
              <SafeIcon icon={FiPlayCircle} /> {wantToLearnMore.buttonText}
            </SmartLink>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}